import type { VehicleStatus } from '@hetivo/hetivo-lib-types';
import { VEHICLE_STATUS } from '@hetivo/hetivo-lib-types';
import { z } from 'zod/v4';

import type { SearchVehiclesParams, VehicleSortableColumn } from '../api';

// ─── Tabs ───────────────────────────────────────────────────────────────────

/**
 * The vehicles-list tabs, in display order (SWOOP-223). `available` and `allocated` are the new
 * allocation tabs; `archived` is kept (a locked product decision) so archived vehicles stay reachable.
 */
export const VEHICLE_TABS = ['all', 'available', 'allocated', 'inService', 'archived'] as const;

export type VehicleTab = (typeof VEHICLE_TABS)[number];

// ─── Filters ──────────────────────────────────────────────────────────────────

/**
 * The full "more filters" state. Array filters hold master-list ids; numeric/date filters hold the
 * range bounds. `statuses` is the quick status filter shown next to the search box (independent from
 * the tab). Dates stay `Date` here (what the action bar's pickers use); the URL schema serialises
 * them to ISO strings.
 */
export interface VehicleFilters {
  energyLevelMax: number | undefined;
  energyLevelMin: number | undefined;
  energySourceIds: string[];
  locationIds: string[];
  makeIds: string[];
  odometerMax: number | undefined;
  odometerMin: number | undefined;
  ownershipIds: string[];
  registrationExpiryFrom: Date | undefined;
  registrationExpiryTo: Date | undefined;
  statuses: VehicleStatus[];
  territoryIds: string[];
  vehicleGroupIds: string[];
  vehicleTypeIds: string[];
}

export const EMPTY_FILTERS: VehicleFilters = {
  energyLevelMax: undefined,
  energyLevelMin: undefined,
  energySourceIds: [],
  locationIds: [],
  makeIds: [],
  odometerMax: undefined,
  odometerMin: undefined,
  ownershipIds: [],
  registrationExpiryFrom: undefined,
  registrationExpiryTo: undefined,
  statuses: [],
  territoryIds: [],
  vehicleGroupIds: [],
  vehicleTypeIds: [],
};

// ─── URL search schema (SWOOP-230) ──────────────────────────────────────────────

const VEHICLE_STATUSES = Object.values(VEHICLE_STATUS);
const stringArray = z.array(z.string()).optional().catch(undefined);
// Ranges mirror the backend DTO's own validation (@Min(0)/@Max(100) for energy, @Min(0) for odometer —
// see packages/hetivo-lib-back-utils/src/mongo/dto/vehicle.dto.ts) so an out-of-range or hand-edited
// URL value falls back to `undefined` here instead of surviving validateSearch and reaching the API,
// where it would 400 rather than being dropped as intended.
const energyLevelNumber = z.number().min(0).max(100).optional().catch(undefined);
const odometerNumber = z.number().min(0).optional().catch(undefined);

/**
 * The vehicles-list URL search-param schema (SWOOP-230). Lifting the list's tab / search / page /
 * sort / filters into the URL is what lets a Back navigation from a vehicle's profile restore the
 * list exactly as it was. Every field is optional + `.catch(undefined)`, so a hand-edited or stale
 * URL degrades to the defaults (applied by the consuming hook) instead of throwing, and a plain
 * navigation to `/vehicles` needs no search object. Shared by both the list route and the
 * vehicle-profile route (whose links carry these params through unchanged), so it must never reject.
 */
export const vehiclesListSearchSchema = z.object({
  energyLevelMax: energyLevelNumber,
  energyLevelMin: energyLevelNumber,
  energySourceIds: stringArray,
  locationIds: stringArray,
  makeIds: stringArray,
  odometerMax: odometerNumber,
  odometerMin: odometerNumber,
  ownershipIds: stringArray,
  page: z.number().int().positive().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  registrationExpiryFrom: z.string().optional().catch(undefined),
  registrationExpiryTo: z.string().optional().catch(undefined),
  sortBy: z.string().optional().catch(undefined),
  sortDir: z.enum(['asc', 'desc']).optional().catch(undefined),
  statuses: stringArray,
  tab: z.enum(VEHICLE_TABS).optional().catch(undefined),
  territoryIds: stringArray,
  vehicleGroupIds: stringArray,
  vehicleTypeIds: stringArray,
});

export type VehiclesListSearch = z.infer<typeof vehiclesListSearchSchema>;

// ─── Conversions ────────────────────────────────────────────────────────────────

/** Parses an ISO date string to a Date, or `undefined` when absent / unparseable. */
function toDate(value: string | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Keeps only the known {@link VehicleStatus} tokens from a raw string array. */
function toStatuses(values: string[] | undefined): VehicleStatus[] {
  if (!values) {
    return [];
  }
  return values.filter((value): value is VehicleStatus => (VEHICLE_STATUSES as string[]).includes(value));
}

/** Narrows a raw sort column string to a {@link VehicleSortableColumn} the backend accepts. */
const SORTABLE_COLUMNS: readonly VehicleSortableColumn[] = [
  'createdAt',
  'licensePlate',
  'odometer',
  'registrationExpiryDate',
  'renewingInsuranceDate',
  'updatedAt',
  'vehicleId',
  'vin',
  'year',
];

function toSortColumn(value: string | undefined): VehicleSortableColumn | undefined {
  return SORTABLE_COLUMNS.find((column) => column === value);
}

/** Builds the display {@link VehicleFilters} object from the URL search params. */
export function searchToFilters(search: VehiclesListSearch): VehicleFilters {
  return {
    energyLevelMax: search.energyLevelMax,
    energyLevelMin: search.energyLevelMin,
    energySourceIds: search.energySourceIds ?? [],
    locationIds: search.locationIds ?? [],
    makeIds: search.makeIds ?? [],
    odometerMax: search.odometerMax,
    odometerMin: search.odometerMin,
    ownershipIds: search.ownershipIds ?? [],
    registrationExpiryFrom: toDate(search.registrationExpiryFrom),
    registrationExpiryTo: toDate(search.registrationExpiryTo),
    statuses: toStatuses(search.statuses),
    territoryIds: search.territoryIds ?? [],
    vehicleGroupIds: search.vehicleGroupIds ?? [],
    vehicleTypeIds: search.vehicleTypeIds ?? [],
  };
}

/** `[]`/`undefined` collapse to `undefined` so the URL stays clean (no empty arrays). */
function arrayOrUndefined(values: string[]): string[] | undefined {
  return values.length > 0 ? values : undefined;
}

/**
 * Serialises the display {@link VehicleFilters} back into the URL search-param fields (dates → ISO
 * strings, empty arrays dropped). Returned as a partial patch merged onto the existing search.
 */
export function filtersToSearch(
  filters: VehicleFilters,
): Pick<
  VehiclesListSearch,
  | 'energyLevelMax'
  | 'energyLevelMin'
  | 'energySourceIds'
  | 'locationIds'
  | 'makeIds'
  | 'odometerMax'
  | 'odometerMin'
  | 'ownershipIds'
  | 'registrationExpiryFrom'
  | 'registrationExpiryTo'
  | 'statuses'
  | 'territoryIds'
  | 'vehicleGroupIds'
  | 'vehicleTypeIds'
> {
  return {
    energyLevelMax: filters.energyLevelMax,
    energyLevelMin: filters.energyLevelMin,
    energySourceIds: arrayOrUndefined(filters.energySourceIds),
    locationIds: arrayOrUndefined(filters.locationIds),
    makeIds: arrayOrUndefined(filters.makeIds),
    odometerMax: filters.odometerMax,
    odometerMin: filters.odometerMin,
    ownershipIds: arrayOrUndefined(filters.ownershipIds),
    registrationExpiryFrom: filters.registrationExpiryFrom?.toISOString(),
    registrationExpiryTo: filters.registrationExpiryTo?.toISOString(),
    statuses: arrayOrUndefined(filters.statuses),
    territoryIds: arrayOrUndefined(filters.territoryIds),
    vehicleGroupIds: arrayOrUndefined(filters.vehicleGroupIds),
    vehicleTypeIds: arrayOrUndefined(filters.vehicleTypeIds),
  };
}

/** The active sort as the {@link SortParams} the search API expects, or `undefined` when unsorted. */
export function searchToSort(search: VehiclesListSearch): SearchVehiclesParams['sort'] {
  const sortBy = toSortColumn(search.sortBy);
  if (sortBy === undefined || search.sortDir === undefined) {
    return undefined;
  }
  return { sortBy, sortDirection: search.sortDir };
}
